import './utils/disable-global-error-reporter';
import './utils/suppress-web-feature-alerts';
import { StrictMode } from 'react';
import { Log, LogStorage, LOG_CONFIG_TOKEN } from '@servicetitan/log-service';
import {
    BASE_URL_TOKEN_FinancingApi,
    BASE_URL_TOKEN_JobAssistApi,
    FinancingApi,
    FinPlansApi,
    BASE_URL_TOKEN_FinPlansApi,
    SESSION_ID_FinPlansApi,
    TENANT_ID_FinPlansApi,
    REQUEST_ORIGIN_FinPlansApi,
    ENTRY_POINT_FinancingApi,
    SOURCE_MFE_FinancingApi,
    ENTRY_POINT_FinPlansApi,
    SOURCE_MFE_FinPlansApi,
    ACTING_USER_ID_FinPlansApi,
    JobAssistApi,
    REQUEST_ORIGIN_FinancingApi,
    RequestOrigin,
    SESSION_ID_FinancingApi,
    TENANT_ID_FinancingApi,
    LoanApplicationSubmitSource2,
    ACTING_USER_ID_FinancingApi,
} from '@servicetitan/financing-api-client';
import {
    BASE_URL_TOKEN_PrequalsApi,
    PrequalsApi,
    REQUEST_ORIGIN_PrequalsApi,
    ENTRY_POINT_PrequalsApi,
    SOURCE_MFE_PrequalsApi,
    SESSION_ID_PrequalsApi,
    TENANT_ID_PrequalsApi,
} from '@servicetitan/financing-api-client/src/prequals-api';
import {
    BASE_URL_TOKEN_FintechDataApi,
    FintechDataApi,
    REQUEST_ORIGIN_FintechDataApi,
    ENTRY_POINT_FintechDataApi,
    SOURCE_MFE_FintechDataApi,
} from '@servicetitan/financing-api-client/src/fintech-data-api';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Container as LayoutContainer } from './features/layout/Container';
import {
    ErrorBoundary,
    WithProviders,
    getLDClientId,
    ExternalLinkHandler,
    MfeFirstPaintProvider,
    useAxiosFullStoryInterceptor,
    useRequestAttributionRum,
    type RequestAttributionProps,
} from '@servicetitan/financing-utils-shared';
import {
    FinancedAmountStore,
    AmountRangeStore,
    SelectedPlanStore,
    CustomPlansStore,
    PrequalsStore,
} from '@servicetitan/financing-plans-shared';
import {
    ApplicationDataStore,
    ApplicationDataDefaultValues,
    getApplicationDataDefaultValues,
    DisclosureDataStore,
    DynamicDisclosureStore,
    SubmittingUserStore,
    ExternalApplicationStore,
    PreApprovalLoader,
} from '@servicetitan/financing-application-shared';
import { Provider } from '@servicetitan/react-ioc';
import { AnvilProvider, ThemeProviderProps } from '@servicetitan/anvil2';
import { themeLight, useViewportFitCover } from '@servicetitan/web-feature-ui';

import './overrides.css';
import { SetSetupData } from './SetSetupData';
import { CustomerDataLoadingStore } from './CustomerDataLoadingStore';
import { WorkflowButtonsProvider } from './providers/WorkflowButtonsProvider';
import { WorkflowLayoutsProvider } from './providers/WorkflowLayoutsProvider';
import { ExternalApplicationWorkflowProvider } from './providers/ExternalApplicationWorkflowProvider';

import {
    ConsentDataStore,
    DecisionDataStore,
    PrequalificationDecision,
    SignatureDataStore,
    OfferAcceptedStore,
} from '@servicetitan/financing-decision-shared';
import {
    SetupDataStore,
    WorkflowStore,
    PrequalificationResponseStore,
    SecondLookServiceDegradedStore,
    SecondLookApplicationSubmittedStore,
} from '@servicetitan/financing-workflow-shared';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import { FinancingPageWrapper } from './common/FinancingPageWrapper';
import { IonReactRouter } from '@ionic/react-router';
import { setupIonicReact } from '@ionic/react';
import {
    AppLoadingState,
    App as WebFeatureAppService,
} from '@servicetitan/web-feature-app-service';

setupIonicReact({
    mode: 'md',
});
const theme: ThemeProviderProps['theme'] = {
    ...themeLight,
    component: {
        ButtonPrimaryForegroundColor: { value: '#D9D9D9' },
        ButtonPrimaryForegroundColorHover: { value: '#D9D9D9' },
        ButtonPrimaryForegroundColorActive: { value: '#D9D9D9' },
        ButtonPrimaryBackgroundColor: { value: '#141414' },
        ButtonPrimaryBackgroundColorHover: { value: '#3A3A3A' },
        ButtonPrimaryBackgroundColorActive: { value: '#141414' },
        ButtonSecondaryBackgroundColor: { value: 'transparent' },
        ButtonSecondaryBackgroundColorHover: { value: 'transparent' },
        ButtonSecondaryBorderColor: { value: '#000' },
    },
};

const getBasename = () => {
    const pathname = window.location.pathname;

    // Check for known base paths
    if (pathname.startsWith('/technician-application')) {
        return '/technician-application';
    }

    if (pathname.startsWith('/application')) {
        return '/application';
    }

    // Generic fallback: extract base path if URL follows pattern /{base}/loans/...
    // This handles any future base path without code changes
    const basePathMatch = pathname.match(/^(\/[^\/]+)\/loans/);
    if (basePathMatch) {
        return basePathMatch[1];
    }

    // If no base path detected (e.g., running at root), return empty string
    return '';
};

interface AppProps extends ApplicationDataDefaultValues, RequestAttributionProps {
    provider: string;
    buId: string;
    tenantId: string;
    technicianId: string;
    jobId: string;
    customerId: string;
    estimateAmount: number;
    serviceLocationState: string;
    showHeader: boolean;
    useInAppBrowser?: boolean;
}

const queryClient = new QueryClient({
    // Ensure that all queries and mutations throw an error when they fail so errors
    // will be caught and handled by the error boundary
    defaultOptions: {
        queries: {
            throwOnError: true,
        },
        mutations: {
            throwOnError: true,
        },
    },
});

export const App = (props: AppProps) => {
    useViewportFitCover();
    useAxiosFullStoryInterceptor();
    WebFeatureAppService.loading({ state: AppLoadingState.Finished });
    const sessionId = window.crypto.randomUUID();
    const requestOrigin = props.origin ?? RequestOrigin.FMA;

    useRequestAttributionRum({
        rumServiceName: 'financing-technician-application',
        origin: requestOrigin,
        entryPoint: props.entryPoint,
        sourceMfe: props.sourceMfe,
    });

    const providers = {
        singletons: [
            Log,
            LogStorage,
            {
                provide: LOG_CONFIG_TOKEN,
                useValue: {
                    endpoint: '/api/log',
                    storageKey: 'LogStorage_TechnicianApplication',
                },
            },
            FinancingApi,
            FinPlansApi,
            JobAssistApi,
            {
                provide: BASE_URL_TOKEN_FinancingApi,
                useValue: '/api/financing',
            },
            {
                provide: BASE_URL_TOKEN_JobAssistApi,
                useValue: '/api/servicetitan/',
            },
            {
                provide: SESSION_ID_FinancingApi,
                useValue: sessionId,
            },
            {
                provide: TENANT_ID_FinancingApi,
                useValue: props.tenantId,
            },
            {
                provide: REQUEST_ORIGIN_FinancingApi,
                useValue: requestOrigin,
            },
            {
                provide: ENTRY_POINT_FinancingApi,
                useValue: props.entryPoint,
            },
            {
                provide: SOURCE_MFE_FinancingApi,
                useValue: props.sourceMfe,
            },
            {
                provide: ACTING_USER_ID_FinancingApi,
                useValue: props.technicianId,
            },
            {
                provide: BASE_URL_TOKEN_FinPlansApi,
                useValue: '/api/financing',
            },
            {
                provide: SESSION_ID_FinPlansApi,
                useValue: sessionId,
            },
            {
                provide: TENANT_ID_FinPlansApi,
                useValue: props.tenantId,
            },
            {
                provide: REQUEST_ORIGIN_FinPlansApi,
                useValue: requestOrigin,
            },
            {
                provide: ENTRY_POINT_FinPlansApi,
                useValue: props.entryPoint,
            },
            {
                provide: SOURCE_MFE_FinPlansApi,
                useValue: props.sourceMfe,
            },
            {
                provide: ACTING_USER_ID_FinPlansApi,
                useValue: props.technicianId,
            },
            PrequalsApi,
            {
                provide: BASE_URL_TOKEN_PrequalsApi,
                useValue: '/api/prequals',
            },
            {
                provide: SESSION_ID_PrequalsApi,
                useValue: sessionId,
            },
            {
                provide: TENANT_ID_PrequalsApi,
                useValue: props.tenantId,
            },
            {
                provide: REQUEST_ORIGIN_PrequalsApi,
                useValue: requestOrigin,
            },
            {
                provide: ENTRY_POINT_PrequalsApi,
                useValue: props.entryPoint,
            },
            {
                provide: SOURCE_MFE_PrequalsApi,
                useValue: props.sourceMfe,
            },
            FintechDataApi,
            {
                provide: BASE_URL_TOKEN_FintechDataApi,
                useValue: '/api/fintech-data',
            },
            {
                provide: REQUEST_ORIGIN_FintechDataApi,
                useValue: requestOrigin,
            },
            {
                provide: ENTRY_POINT_FintechDataApi,
                useValue: props.entryPoint,
            },
            {
                provide: SOURCE_MFE_FintechDataApi,
                useValue: props.sourceMfe,
            },
        ],
    };

    return (
        <StrictMode>
            <MfeFirstPaintProvider mfeName="technician-application">
                <LDProvider
                    clientSideID={getLDClientId()}
                    context={{
                        kind: 'multi',
                        tenant: {
                            key: props.tenantId,
                        },
                    }}
                >
                    <QueryClientProvider client={queryClient}>
                        <WithProviders
                            providers={[
                                FinancedAmountStore.Provider,
                                AmountRangeStore.Provider,
                                SetupDataStore.Provider,
                                SecondLookServiceDegradedStore.Provider,
                                SecondLookApplicationSubmittedStore.Provider,
                                SelectedPlanStore.Provider,
                                CustomPlansStore.Provider,
                                SignatureDataStore.Provider,
                                WorkflowStore.Provider,
                                DecisionDataStore.Provider,
                                ConsentDataStore.Provider,
                                OfferAcceptedStore.Provider,
                                DisclosureDataStore.Provider,
                                DynamicDisclosureStore.Provider,
                                ExternalApplicationStore.Provider,
                                WorkflowButtonsProvider,
                                WorkflowLayoutsProvider,
                                ExternalApplicationWorkflowProvider,
                                SubmittingUserStore.Provider,
                                PrequalsStore.Provider,
                                PrequalificationDecision.PrequalificationDecisionDataStore.Provider,
                                PrequalificationResponseStore.Provider,
                                CustomerDataLoadingStore.Provider,
                                [
                                    ApplicationDataStore.Provider,
                                    {
                                        initialState: getApplicationDataDefaultValues({
                                            ...props,
                                            source: LoanApplicationSubmitSource2.Mobile,
                                        }),
                                    },
                                ],
                            ]}
                        >
                            <Provider {...providers}>
                                <IonReactRouter basename={getBasename()}>
                                    <FinancingPageWrapper showHeaders={props.showHeader}>
                                        <ErrorBoundary actionCausingError="loading the page">
                                            <SetSetupData {...props}>
                                                <ExternalLinkHandler useInAppBrowser={false}>
                                                    <AnvilProvider
                                                        themeData={{ theme, mode: 'light' }}
                                                        style={{
                                                            height: '100%',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            padding: '1rem',
                                                        }}
                                                    >
                                                        <PreApprovalLoader>
                                                            <LayoutContainer />
                                                        </PreApprovalLoader>
                                                    </AnvilProvider>
                                                </ExternalLinkHandler>
                                            </SetSetupData>
                                        </ErrorBoundary>
                                    </FinancingPageWrapper>
                                </IonReactRouter>
                            </Provider>
                        </WithProviders>
                    </QueryClientProvider>
                </LDProvider>
            </MfeFirstPaintProvider>
        </StrictMode>
    );
};
